///
/// EarthBrowser
///
/// City Label Node - Focusable City Data Container
///
/// Holds city feature data and computed properties for the city labels layer
/// Screen-space rendering is handled by CityLabelsOverlay
/// Supports focus/zoom like other feature nodes
///
/// Created by Matt Giger on Dec 25, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { GroundFocusAdapter, populationFramingRadius } from '@/earthbrowser/datasets/GroundFocusAdapter'
import { type InfoPanelData, type InfoPanelProvider } from '@/earthbrowser/datasets/InfoPanelProvider'
import { type EarthBrowserContext } from '@/earthbrowser/EarthBrowserContext'
import { geographic } from '@/earthbrowser/utils/geographic'
import { engineBridge } from '@/earthbrowser/engineBridge'
import { registerCityVignetteDebug } from '@/earthbrowser/utils/tileStatsRegistry'
import { pushTargetToHistory } from '@/utils/navigationHistory'
import type { LocationsDatasetLayer } from './LocationsDatasetLayer'
import { CityWeatherVignetteNode } from './vignette/CityWeatherVignetteNode'
import type { Feature, Geometry } from '@shared-types/geojson'
import type { Camera } from '@stovokor/camera/Camera'
import type { RenderState } from '@stovokor/engine/RenderState'
import type { FocusTransitionConfig } from '@stovokor/gestures/FocusTarget'
import { GestureNode } from '@stovokor/gestures/GestureNode'
import type { GestureHitResult } from '@stovokor/gestures/GestureRecognizer'
import { trig } from '@stovokor/math/math'
import { sphere } from '@stovokor/math/sphere'
import { vec3 } from '@stovokor/math/vector'
import { matrix } from '@stovokor/math/matrix'
import type { CityImportance, CityProperties } from './city-types'
import { CITY_DISTANCE_THRESHOLDS, CITY_RANK_DEFAULT } from './city-types'

const cHitSphereRadius = 1 // km - hit detection radius

/**
 * Fallback framing radius (km) per importance tier, used when a city has no
 * population data. With population available the radius is derived from it,
 * so large metros frame wide and towns frame close.
 */
const cTierFramingRadius: Record<CityImportance, number> = {
	capital: 25,
	major: 15,
	medium: 8,
	small: 4,
	town: 2.5
}

/**
 * City Label Node
 *
 * Focusable data container for a city feature that:
 * 1. Stores city properties and geographic location
 * 2. Provides cartesian position for screen projection
 * 3. Computes distance-based visibility parameters
 * 4. On selection, frames the city's extent (population-derived radius) and
 *    leaves the user in standard Earth navigation - no entity-orbit camera
 *
 * Actual 2D rendering is performed by CityLabelsOverlay
 */
export class CityLabelNode extends GestureNode implements InfoPanelProvider {
	public readonly id: string
	public readonly focusable: boolean = true
	public feature: Feature<Geometry | null, CityProperties>

	private properties: CityProperties
	private importance: CityImportance
	private cameraAdapter: GroundFocusAdapter
	private hitSphere = new sphere(cHitSphereRadius)
	// Live-conditions diorama shown while focused (see vignette/)
	private vignette?: CityWeatherVignetteNode
	private readonly ebContext: EarthBrowserContext

	// Geographic location
	private geo: geographic = new geographic(0, 0, 0)
	private cartesian: vec3 = vec3.zero
	private lonDeg: number = 0
	private latDeg: number = 0

	// Distance thresholds for this importance tier
	private distanceNear: number
	private distanceFar: number

	constructor(context: EarthBrowserContext, feature: Feature<Geometry | null, CityProperties>) {
		super(context)
		this.ebContext = context
		this.feature = feature
		this.properties = feature.properties
		this.id = `city-label-${this.properties.geonameid}`
		this.importance = this.properties.importance

		// Get distance thresholds for this importance tier
		const thresholds = CITY_DISTANCE_THRESHOLDS[this.importance]
		this.distanceNear = thresholds.near
		this.distanceFar = thresholds.far

		// Extract coordinates from GeoJSON (city features are always Points)
		const coords = feature.geometry?.type === 'Point' ? feature.geometry.coordinates : undefined
		if (!coords || coords.length < 2) {
			console.warn(`[CityLabelNode] Invalid geometry for city ${this.properties.name}`)
			this.cameraAdapter = new GroundFocusAdapter(context, this, this.cartesian, this.framingRadius())
			return
		}

		const [lon, lat, alt] = coords
		this.lonDeg = lon
		this.latDeg = lat
		const elevation = alt ?? this.properties.alt ?? 0

		// Create geographic position at surface level
		this.geo = new geographic(
			lon * trig.rad,
			lat * trig.rad,
			elevation / 1000  // Convert to km
		)
		this.cartesian = this.geo.cartesian

		// Position the node in 3D space for hit detection
		this.transform = matrix.translate(this.cartesian)

		this.cameraAdapter = new GroundFocusAdapter(context, this, this.cartesian, this.framingRadius())
	}

	/**
	 * Extent radius (km) this city should be framed at when selected,
	 * derived from population with a per-tier fallback
	 */
	private framingRadius(): number {
		return populationFramingRadius(this.properties.pop, cTierFramingRadius[this.importance])
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		// Current conditions + nearby webcams/buoys/aurora from the locations
		// layer's caches (drives the hover callout's weather detail)
		const layerManager = engineBridge.getState()?.datasetScene.layerManager
		const locationsLayer = layerManager?.getLayer('locations') as LocationsDatasetLayer | undefined
		const hoverContext = locationsLayer?.getCityHoverContext(
			this.properties.geonameid,
			this.properties.lat ?? this.latDeg,
			this.properties.lon ?? this.lonDeg
		)

		return {
			title: this.properties.name,
			subtitle: this.properties.admin1 ? `${this.properties.admin1}, ${this.properties.country}` : this.properties.country,
			theme: "location",
			contentType: "location",
			contentData: {
				type: "location",
				locationInfo: {
					geoid: this.properties.geonameid,
					name: this.properties.name,
					country: this.properties.country,
					admin1: this.properties.admin1 ?? "",
					admin2: this.properties.admin2 ?? "",
					tz: this.properties.tz ?? "",
					pop: this.properties.pop ?? 0,
				lon: this.properties.lon ?? this.lonDeg,
				lat: this.properties.lat ?? this.latDeg,
					alt: this.properties.alt ?? 0,
					code: this.properties.code ?? ""
				},
				countryName: null, // Could be looked up from CountriesTable if needed
				weather: hoverContext?.weather ?? null,
				nearby: hoverContext?.nearby ?? null
			}
		}
	}

	// ========== Focus Lifecycle ==========

	/** Focus from a tap on the city's screen label (per-click zoom cap applies) */
	public requestTapFocus(): boolean {
		return this.cameraAdapter.requestTapFocus()
	}

	public onFocusGained(camera: Camera): void {
		// Hand camera control straight back to standard Earth navigation -
		// selection keeps the info panel/callout, but there is no modal
		// entity-orbit camera for cities
		this.cameraAdapter.attach(camera)
		this.showVignette()

		// Push to browser history for back/forward navigation
		// Use 'geoid' to match the target reader in PageContent (and search results)
		pushTargetToHistory('geoid', String(this.properties.geonameid))
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.hideVignette()
	}

	// ========== Weather Vignette ==========

	/**
	 * Attach the live-conditions diorama once the framing flight has landed.
	 * Only when city weather is allowed here (grid download permitted and
	 * the city weather layer on); the vignette itself stays empty until a
	 * sample arrives, so it never shows a bare plinth.
	 */
	private showVignette(): void {
		const layerManager = engineBridge.getState()?.datasetScene.layerManager
		const locationsLayer = layerManager?.getLayer('locations') as LocationsDatasetLayer | undefined
		if (locationsLayer && !locationsLayer.cityWeatherAllowed) {
			return
		}
		if (!this.vignette) {
			this.vignette = new CityWeatherVignetteNode(this.ebContext, this.geo, this.framingRadius(), this.cameraAdapter.landingDistance)
			this.addNode(this.vignette)
		}
		const vignette = this.vignette
		vignette.show()
		registerCityVignetteDebug({
			snapshot: () => vignette.snapshot(),
			override: weather => vignette.setWeatherOverride(weather),
		})
	}

	private hideVignette(): void {
		const vignette = this.vignette
		if (!vignette) return
		registerCityVignetteDebug(undefined)
		vignette.hide(() => {
			if (this.vignette === vignette) {
				this.vignette = undefined
			}
			this.removeNode(vignette)
			vignette.destroy()
		})
	}

	override destroy(): void {
		if (this.vignette) {
			registerCityVignetteDebug(undefined)
			this.vignette = undefined
		}
		super.destroy()
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		// Static target: framing depends on the camera's pre-flight position
		// (approach azimuth), so compute it once rather than per-frame
		return {
			target: this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: this.cameraAdapter.transitionDuration(camera),
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	// ========== Gesture Hit Detection ==========

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.hitSphere.intersect(state.ray)
		if (dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}

	/**
	 * Calculate alpha based on camera altitude
	 * Returns 0 if the city is outside visible range
	 */
	public calculateAlpha(cameraAltitudeKm: number): number {
		// Outside visible range
		if (cameraAltitudeKm > this.distanceFar) {
			return 0
		}

		const clampedDistance = Math.max(cameraAltitudeKm, this.distanceNear)

		// Calculate normalized distance (0 = near, 1 = far)
		// Use logarithmic scale for better distribution across large ranges
		const logNear = Math.log(this.distanceNear)
		const logFar = Math.log(this.distanceFar)
		const logDistance = Math.log(clampedDistance)
		const t = (logDistance - logNear) / (logFar - logNear)

		// Clamp to [0, 1]
		const tClamped = Math.max(0, Math.min(1, t))

		// Fade out as we approach the far threshold
		let alpha = 1.0
		if (tClamped > 0.85) {
			alpha = (1.0 - tClamped) / 0.15
			alpha = Math.max(0, Math.min(1, alpha))
			alpha = alpha * alpha * (3 - 2 * alpha)  // Smoothstep
		}

		return alpha
	}

	public get geographicPosition(): geographic {
		return this.geo
	}

	public get cartesianPosition(): vec3 {
		return this.cartesian
	}

	public get importanceTier(): CityImportance {
		return this.importance
	}

	public get population(): number {
		return this.properties.pop ?? 0
	}

	/** Spatial-dominance label rank (0 = most important) */
	public get rank(): number {
		return this.properties.rank ?? CITY_RANK_DEFAULT
	}

	public get name(): string {
		return this.properties.name
	}

	/**
	 * Update feature data (for incremental sync)
	 */
	public updateFeature(feature: Feature<Geometry | null, CityProperties>): void {
		this.feature = feature
		this.properties = feature.properties
		this.cameraAdapter.setFramingRadius(this.framingRadius())
	}
}
